import {
  useGetLocationsQuery,
  useGetPhlebotomistStatsQuery,
  useGetRegionsQuery,
  useGetUsersListQuery,
  UserRole,
} from 'gql/types';
import { useMemo } from 'react';

import {
  ComparisonStats,
  DateRangeType,
  DiscardReasonCount,
  DonationRecord,
  LocationOption,
  PhlebotomistOption,
  PhlebotomistStats,
  RegionOption,
} from '../../types/phlebotomist-dashboard';

// All date math here operates on UTC-midnight Dates produced by
// getDateRangeBounds. Using local-time getters/setters on those Dates
// silently shifts the window by the browser timezone offset (in
// US timezones this can collapse short ranges like "yesterday" to a
// few hours of UTC nighttime — outside donation hours — and emit empty
// results). Stick to UTC mutators/getters throughout.

// Helper to get the start of the calendar week (Monday) at UTC midnight
function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  // Shift so Monday=0: (day + 6) % 7 gives days since Monday
  const daysSinceMonday = (day + 6) % 7;
  d.setUTCDate(d.getUTCDate() - daysSinceMonday);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

// Helper to get the start of the calendar quarter at UTC midnight
function getStartOfQuarter(date: Date): Date {
  const quarter = Math.floor(date.getUTCMonth() / 3);
  return new Date(Date.UTC(date.getUTCFullYear(), quarter * 3, 1, 0, 0, 0, 0));
}

// Get date range bounds based on type.
// `now` may be pinned by the caller so that getDateRangeBounds and
// getFullPeriodEnd resolve the same "current day" — without it, two independent
// new Date() calls can straddle UTC midnight on the 1st of a month and disagree
// about which month is "last month" (e.g. start in April, end in May).
export function getDateRangeBounds(
  dateRange: DateRangeType,
  customRange?: { start: Date; end: Date },
  now: Date = new Date()
): { start: Date; end: Date } {
  const today = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
  // Snap "now" to end-of-day UTC so all consumers get a stable boundary
  const endOfToday = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      23,
      59,
      59,
      999
    )
  );

  switch (dateRange) {
    // ===== ROLLING PERIODS =====
    case 'day':
    case 'dayCal':
      return { start: today, end: endOfToday };

    case 'yesterday':
    case 'yesterdayCal': {
      // `today` is built at UTC midnight, so mutate in UTC to avoid
      // collapsing the window in non-UTC browser timezones.
      const yesterdayStart = new Date(today);
      yesterdayStart.setUTCDate(today.getUTCDate() - 1);
      const yesterdayEnd = new Date(yesterdayStart);
      yesterdayEnd.setUTCHours(23, 59, 59, 999);
      return { start: yesterdayStart, end: yesterdayEnd };
    }

    case 'week': {
      const weekStart = new Date(today);
      weekStart.setUTCDate(today.getUTCDate() - 6);
      return { start: weekStart, end: endOfToday };
    }

    case 'lastWeek': {
      const lastWeekEnd = new Date(today);
      lastWeekEnd.setUTCDate(today.getUTCDate() - 7);
      lastWeekEnd.setUTCHours(23, 59, 59, 999);
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setUTCDate(lastWeekEnd.getUTCDate() - 6);
      lastWeekStart.setUTCHours(0, 0, 0, 0);
      return { start: lastWeekStart, end: lastWeekEnd };
    }

    case 'month': {
      // Rolling: 30 days before today to now
      const monthStart = new Date(today);
      monthStart.setUTCDate(today.getUTCDate() - 29);
      return { start: monthStart, end: endOfToday };
    }

    case 'lastMonth': {
      // Rolling: 60 days before today to 30 days before today 23:59:59
      const lastMonthEnd = new Date(today);
      lastMonthEnd.setUTCDate(today.getUTCDate() - 30);
      lastMonthEnd.setUTCHours(23, 59, 59, 999);
      const lastMonthStart = new Date(lastMonthEnd);
      lastMonthStart.setUTCDate(lastMonthEnd.getUTCDate() - 29);
      lastMonthStart.setUTCHours(0, 0, 0, 0);
      return { start: lastMonthStart, end: lastMonthEnd };
    }

    case 'quarter': {
      // Rolling: 90 days before today to now
      const quarterStart = new Date(today);
      quarterStart.setUTCDate(today.getUTCDate() - 89);
      return { start: quarterStart, end: endOfToday };
    }

    case 'lastQuarter': {
      // Rolling: 180 days before today to 90 days before today 23:59:59
      const lastQuarterEnd = new Date(today);
      lastQuarterEnd.setUTCDate(today.getUTCDate() - 90);
      lastQuarterEnd.setUTCHours(23, 59, 59, 999);
      const lastQuarterStart = new Date(lastQuarterEnd);
      lastQuarterStart.setUTCDate(lastQuarterEnd.getUTCDate() - 89);
      lastQuarterStart.setUTCHours(0, 0, 0, 0);
      return { start: lastQuarterStart, end: lastQuarterEnd };
    }

    case 'year': {
      // Rolling: today minus 1 year to now
      const yearStart = new Date(
        Date.UTC(
          today.getUTCFullYear() - 1,
          today.getUTCMonth(),
          today.getUTCDate()
        )
      );
      return { start: yearStart, end: endOfToday };
    }

    case 'lastYear': {
      // Rolling: today minus 2 years to today minus 1 year 23:59:59
      const lastYearEnd = new Date(
        Date.UTC(
          today.getUTCFullYear() - 1,
          today.getUTCMonth(),
          today.getUTCDate()
        )
      );
      lastYearEnd.setUTCHours(23, 59, 59, 999);
      const lastYearStart = new Date(
        Date.UTC(
          today.getUTCFullYear() - 2,
          today.getUTCMonth(),
          today.getUTCDate()
        )
      );
      return { start: lastYearStart, end: lastYearEnd };
    }

    // ===== CALENDAR PERIODS =====
    case 'weekCal': {
      // start = this week's Monday 00:00, end = now
      const weekStart = getStartOfWeek(today);
      return { start: weekStart, end: endOfToday };
    }

    case 'lastWeekCal': {
      // start = last week's Monday 00:00, end = last week's Sunday 23:59:59
      const thisWeekStart = getStartOfWeek(today);
      const lastWeekSunday = new Date(thisWeekStart);
      lastWeekSunday.setUTCDate(lastWeekSunday.getUTCDate() - 1);
      lastWeekSunday.setUTCHours(23, 59, 59, 999);
      const lastWeekMonday = getStartOfWeek(lastWeekSunday);
      return { start: lastWeekMonday, end: lastWeekSunday };
    }

    case 'monthCal': {
      const monthStart = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)
      );
      return { start: monthStart, end: endOfToday };
    }

    case 'lastMonthCal': {
      // Day 0 of current month = last day of previous month
      const lastMonthEnd = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0)
      );
      lastMonthEnd.setUTCHours(23, 59, 59, 999);
      const lastMonthStart = new Date(
        Date.UTC(lastMonthEnd.getUTCFullYear(), lastMonthEnd.getUTCMonth(), 1)
      );
      return { start: lastMonthStart, end: lastMonthEnd };
    }

    case 'quarterCal': {
      const quarterStart = getStartOfQuarter(today);
      return { start: quarterStart, end: endOfToday };
    }

    case 'lastQuarterCal': {
      const thisQuarterStart = getStartOfQuarter(today);
      const lastQuarterEnd = new Date(thisQuarterStart);
      lastQuarterEnd.setUTCDate(lastQuarterEnd.getUTCDate() - 1);
      lastQuarterEnd.setUTCHours(23, 59, 59, 999);
      const lastQuarterStart = getStartOfQuarter(lastQuarterEnd);
      return { start: lastQuarterStart, end: lastQuarterEnd };
    }

    case 'yearCal': {
      const yearStart = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
      return { start: yearStart, end: endOfToday };
    }

    case 'pYearCal': {
      const pYearStart = new Date(Date.UTC(today.getUTCFullYear() - 1, 0, 1));
      const pYearEnd = new Date(
        Date.UTC(today.getUTCFullYear() - 1, 11, 31, 23, 59, 59, 999)
      );
      return { start: pYearStart, end: pYearEnd };
    }

    case 'custom': {
      if (!customRange) return { start: today, end: endOfToday };
      const customEnd = new Date(customRange.end);
      // `customRange` comes from the picker as local-time Dates representing
      // calendar dates the user selected — compare in local time, but emit
      // the end-of-day boundary in UTC so the query window matches stored
      // UTC donationDate values.
      const isEndToday =
        customEnd.getUTCFullYear() === today.getUTCFullYear() &&
        customEnd.getUTCMonth() === today.getUTCMonth() &&
        customEnd.getUTCDate() === today.getUTCDate();
      if (!isEndToday) {
        customEnd.setUTCHours(23, 59, 59, 999);
      }
      return { start: new Date(customRange.start), end: customEnd };
    }

    default:
      return { start: today, end: endOfToday };
  }
}

/**
 * Get the full period end date for a date range type.
 * Unlike getDateRangeBounds which returns 'now' for current periods,
 * this returns the actual end of the period (e.g., end of month, end of quarter).
 * Used for calculating total working days in a period for goal calculations.
 */
export function getFullPeriodEnd(
  dateRange: DateRangeType,
  customRange?: { start: Date; end: Date },
  now: Date = new Date()
): Date {
  const today = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );

  // Helper to get end of week (Sunday 23:59:59 UTC, Mon-Sun week)
  const getEndOfWeek = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    // Days until Sunday: if Sunday(0) → 0, Mon(1) → 6, Tue(2) → 5, etc.
    const daysUntilSunday = day === 0 ? 0 : 7 - day;
    d.setUTCDate(d.getUTCDate() + daysUntilSunday);
    d.setUTCHours(23, 59, 59, 999);
    return d;
  };

  // Helper to get end of month at UTC 23:59:59.999
  const getEndOfMonth = (date: Date): Date => {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth() + 1,
        0,
        23,
        59,
        59,
        999
      )
    );
  };

  // Helper to get end of quarter at UTC 23:59:59.999
  const getEndOfQuarter = (date: Date): Date => {
    const quarter = Math.floor(date.getUTCMonth() / 3);
    return new Date(
      Date.UTC(date.getUTCFullYear(), (quarter + 1) * 3, 0, 23, 59, 59, 999)
    );
  };

  // Helper to get end of year at UTC 23:59:59.999
  const getEndOfYear = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), 11, 31, 23, 59, 59, 999));
  };

  switch (dateRange) {
    // Rolling periods - end is relative to today, so use now
    case 'day':
    case 'dayCal':
    case 'yesterday':
    case 'yesterdayCal':
    case 'week':
    case 'lastWeek':
    case 'month':
    case 'lastMonth':
    case 'quarter':
    case 'lastQuarter':
    case 'year':
    case 'lastYear':
      return getDateRangeBounds(dateRange, customRange, now).end;

    // Calendar periods - return actual period end
    case 'weekCal':
      return getEndOfWeek(today);

    case 'lastWeekCal': {
      const thisWeekStart = getStartOfWeek(today);
      const lastWeekEnd = new Date(thisWeekStart);
      lastWeekEnd.setUTCDate(lastWeekEnd.getUTCDate() - 1);
      lastWeekEnd.setUTCHours(23, 59, 59, 999);
      return lastWeekEnd;
    }

    case 'monthCal':
      return getEndOfMonth(today);

    case 'lastMonthCal': {
      const lastMonthEnd = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0)
      );
      lastMonthEnd.setUTCHours(23, 59, 59, 999);
      return lastMonthEnd;
    }

    case 'quarterCal':
      return getEndOfQuarter(today);

    case 'lastQuarterCal': {
      const thisQuarterStart = getStartOfQuarter(today);
      const lastQuarterEnd = new Date(thisQuarterStart);
      lastQuarterEnd.setUTCDate(lastQuarterEnd.getUTCDate() - 1);
      lastQuarterEnd.setUTCHours(23, 59, 59, 999);
      return lastQuarterEnd;
    }

    case 'yearCal':
      return getEndOfYear(today);

    case 'pYearCal': {
      return new Date(
        Date.UTC(today.getUTCFullYear() - 1, 11, 31, 23, 59, 59, 999)
      );
    }

    case 'custom':
      return customRange?.end || now;

    default:
      return now;
  }
}

// Convert backend donation record to frontend format
function transformDonationRecord(record: {
  id: string;
  shortId: number;
  date: string;
  donorName: string;
  donorId: string;
  type: string;
  approvedML: number;
  actualML?: number | null;
  collectionSet?: string | null;
  veinSide?: string | null;
  donationUnit?: string | null;
  examinationNotes?: string | null;
  ineligibleReason?: string | null;
  expectedPRBC: number;
  expectedFFPSingle: number;
  expectedFFPDouble: number;
  actualPRBC: number;
  actualFFPSingle: number;
  actualFFPDouble: number;
  status: string;
  phlebotomistName: string;
  locationName: string;
  regionName: string;
  failureReasons: string[];
  products: {
    type: string;
    status: string;
    orderId?: number | null;
    discardReason?: string | null;
  }[];
}): DonationRecord {
  return {
    id: record.id,
    shortId: record.shortId,
    date: new Date(record.date),
    donorName: record.donorName,
    donorId: record.donorId,
    type: record.type as 'Single' | 'Double',
    approvedML: record.approvedML,
    actualML: record.actualML ?? null,
    collectionSet: record.collectionSet ?? null,
    veinSide: record.veinSide ?? null,
    donationUnit: record.donationUnit ?? null,
    examinationNotes: record.examinationNotes ?? null,
    ineligibleReason: record.ineligibleReason ?? null,
    expectedPRBC: record.expectedPRBC,
    expectedFFPSingle: record.expectedFFPSingle,
    expectedFFPDouble: record.expectedFFPDouble,
    actualPRBC: record.actualPRBC,
    actualFFPSingle: record.actualFFPSingle,
    actualFFPDouble: record.actualFFPDouble,
    status: record.status as DonationRecord['status'],
    phlebotomistName: record.phlebotomistName,
    locationName: record.locationName,
    regionName: record.regionName,
    failureReasons: record.failureReasons || [],
    products: record.products || [],
  };
}

// Aggregate multiple stats into comparison stats
function aggregateStats(
  stats: PhlebotomistStats[],
  label: string
): ComparisonStats {
  if (stats.length === 0) {
    return {
      label,
      overall: {
        drawSuccessRate: 0,
        attempted: 0,
        failures: 0,
        unitSuccessRate: 0,
        expected: 0,
        actual: 0,
      },
      prbc: { unitSuccessRate: 0, expected: 0, actual: 0 },
      ffp: { unitSuccessRate: 0, expected: 0, actual: 0 },
    };
  }

  const totalAttempted = stats.reduce(
    (sum, s) => sum + s.attemptedDonations,
    0
  );
  const totalSuccessful = stats.reduce(
    (sum, s) => sum + s.successfulDonations,
    0
  );
  const totalPending = stats.reduce(
    (sum, s) => sum + (s.pendingDonations ?? 0),
    0
  );
  // Exclude pending from eligible denominator for draw success rate and failures
  const totalEligible = totalAttempted - totalPending;
  const totalFailures = totalEligible - totalSuccessful;
  const totalExpected = stats.reduce((sum, s) => sum + s.expectedTotal, 0);
  const totalActual = stats.reduce((sum, s) => sum + s.actualTotal, 0);
  const totalExpectedPRBC = stats.reduce((sum, s) => sum + s.expectedPRBC, 0);
  const totalActualPRBC = stats.reduce((sum, s) => sum + s.actualPRBC, 0);
  const totalExpectedFFP = stats.reduce(
    (sum, s) => sum + s.expectedFFPSingle + s.expectedFFPDouble,
    0
  );
  const totalActualFFP = stats.reduce(
    (sum, s) => sum + s.actualFFPSingle + s.actualFFPDouble,
    0
  );

  const drawSuccessRate =
    totalEligible > 0
      ? Math.round((totalSuccessful / totalEligible) * 1000) / 10
      : 0;
  const unitSuccessRate =
    totalExpected > 0
      ? Math.round((totalActual / totalExpected) * 1000) / 10
      : 0;
  const prbcUnitSuccessRate =
    totalExpectedPRBC > 0
      ? Math.round((totalActualPRBC / totalExpectedPRBC) * 1000) / 10
      : 0;
  const ffpUnitSuccessRate =
    totalExpectedFFP > 0
      ? Math.round((totalActualFFP / totalExpectedFFP) * 1000) / 10
      : 0;

  return {
    label,
    overall: {
      drawSuccessRate,
      attempted: totalAttempted,
      pendingDonations: totalPending,
      failures: totalFailures,
      unitSuccessRate,
      expected: totalExpected,
      actual: totalActual,
    },
    prbc: {
      unitSuccessRate: prbcUnitSuccessRate,
      expected: totalExpectedPRBC,
      actual: totalActualPRBC,
    },
    ffp: {
      unitSuccessRate: ffpUnitSuccessRate,
      expected: totalExpectedFFP,
      actual: totalActualFFP,
    },
  };
}

// Aggregate multiple PhlebotomistStats into a single PhlebotomistStats
// (preserves discard reasons, recent donations, and all detail fields)
function aggregateFullStats(
  stats: PhlebotomistStats[],
  label: string
): PhlebotomistStats {
  const empty: PhlebotomistStats = {
    phlebotomistId: '__aggregate__',
    phlebotomistName: label,
    attemptedDonations: 0,
    successfulDonations: 0,
    pendingDonations: 0,
    singleDonors: 0,
    doubleDonors: 0,
    successfulSingleDonors: 0,
    successfulDoubleDonors: 0,
    expectedTotal: 0,
    expectedPRBC: 0,
    expectedPRBCSingle: 0,
    expectedPRBCDouble: 0,
    expectedFFPSingle: 0,
    expectedFFPDouble: 0,
    actualTotal: 0,
    actualPRBC: 0,
    actualPRBCSingle: 0,
    actualPRBCDouble: 0,
    actualFFPSingle: 0,
    actualFFPDouble: 0,
    dblDonorsSglYield: 0,
    overallSuccessRate: 0,
    prbcSuccessRate: 0,
    sglDonorsSuccessRate: 0,
    dblDonorsSuccessRate: 0,
    completelyFailedDonations: 0,
    completelyFailedDiscards: [],
    partiallyFailedDonations: 0,
    partiallyFailedDiscards: [],
    recentDonations: [],
  };
  if (stats.length === 0) return empty;

  const sum = stats.reduce(
    (acc, s) => {
      acc.attemptedDonations += s.attemptedDonations;
      acc.successfulDonations += s.successfulDonations;
      acc.pendingDonations += s.pendingDonations ?? 0;
      acc.singleDonors += s.singleDonors;
      acc.doubleDonors += s.doubleDonors;
      acc.successfulSingleDonors += s.successfulSingleDonors;
      acc.successfulDoubleDonors += s.successfulDoubleDonors;
      acc.expectedTotal += s.expectedTotal;
      acc.expectedPRBC += s.expectedPRBC;
      acc.expectedPRBCSingle += s.expectedPRBCSingle;
      acc.expectedPRBCDouble += s.expectedPRBCDouble;
      acc.expectedFFPSingle += s.expectedFFPSingle;
      acc.expectedFFPDouble += s.expectedFFPDouble;
      acc.actualTotal += s.actualTotal;
      acc.actualPRBC += s.actualPRBC;
      acc.actualPRBCSingle += s.actualPRBCSingle;
      acc.actualPRBCDouble += s.actualPRBCDouble;
      acc.actualFFPSingle += s.actualFFPSingle;
      acc.actualFFPDouble += s.actualFFPDouble;
      acc.dblDonorsSglYield += s.dblDonorsSglYield;
      acc.completelyFailedDonations += s.completelyFailedDonations;
      acc.partiallyFailedDonations += s.partiallyFailedDonations;
      return acc;
    },
    { ...empty }
  );

  // Merge discard reasons
  const mergeDiscards = (arr: DiscardReasonCount[][]): DiscardReasonCount[] => {
    const map = new Map<string, number>();
    for (const list of arr) {
      for (const d of list) {
        map.set(d.reason, (map.get(d.reason) || 0) + d.count);
      }
    }
    return Array.from(map.entries()).map(([reason, count]) => ({
      reason,
      count,
    }));
  };

  sum.completelyFailedDiscards = mergeDiscards(
    stats.map((s) => s.completelyFailedDiscards)
  );
  sum.partiallyFailedDiscards = mergeDiscards(
    stats.map((s) => s.partiallyFailedDiscards)
  );

  // Merge recent donations (combine all, sort by date desc, limit to 50)
  sum.recentDonations = stats
    .flatMap((s) => s.recentDonations)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 50);

  // Compute derived rates
  const eligible = sum.attemptedDonations - sum.pendingDonations;
  sum.overallSuccessRate =
    eligible > 0
      ? Math.round((sum.successfulDonations / eligible) * 1000) / 10
      : 0;
  sum.prbcSuccessRate =
    sum.expectedPRBC > 0
      ? Math.round((sum.actualPRBC / sum.expectedPRBC) * 1000) / 10
      : 0;
  sum.sglDonorsSuccessRate =
    sum.singleDonors > 0
      ? Math.round((sum.successfulSingleDonors / sum.singleDonors) * 1000) / 10
      : 0;
  sum.dblDonorsSuccessRate =
    sum.doubleDonors > 0
      ? Math.round((sum.successfulDoubleDonors / sum.doubleDonors) * 1000) / 10
      : 0;

  return sum;
}

export interface IUsePhlebotomistStatsDataProps {
  dateRange: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  comparisonDateRange?: DateRangeType;
  comparisonCustomDateRange?: { start: Date; end: Date };
  excludedUserIds?: string[];
}

export interface IUsePhlebotomistStatsDataResult {
  phlebotomists: PhlebotomistOption[];
  regions: RegionOption[];
  locations: LocationOption[];
  // Primary date range stats
  allStats: Map<string, PhlebotomistStats>;
  officeStats: (phlebotomistId: string) => ComparisonStats;
  regionStats: (regionId: string) => ComparisonStats;
  multiRegionStats: (regionIds: string[]) => ComparisonStats;
  companyStats: ComparisonStats;
  // Full aggregated stats (with discards, donations) for side-by-side comparison
  regionFullStats: (regionId: string) => PhlebotomistStats;
  multiRegionFullStats: (regionIds: string[]) => PhlebotomistStats;
  companyFullStats: PhlebotomistStats;
  // Comparison date range stats
  comparisonAllStats: Map<string, PhlebotomistStats>;
  comparisonOfficeStats: (phlebotomistId: string) => ComparisonStats;
  comparisonRegionStats: (regionId: string) => ComparisonStats;
  comparisonMultiRegionStats: (regionIds: string[]) => ComparisonStats;
  comparisonCompanyStats: ComparisonStats;
  loading: boolean;
}

export function usePhlebotomistStatsData(
  props: IUsePhlebotomistStatsDataProps
): IUsePhlebotomistStatsDataResult {
  const {
    dateRange,
    customDateRange,
    comparisonDateRange,
    comparisonCustomDateRange,
    excludedUserIds = [],
  } = props;

  // Memoize date bounds to prevent infinite re-renders
  // The dates are converted to ISO strings for stable comparison
  const dateBounds = useMemo(
    () => getDateRangeBounds(dateRange, customDateRange),
    [
      dateRange,
      customDateRange?.start?.getTime(),
      customDateRange?.end?.getTime(),
    ]
  );

  const comparisonDateBounds = useMemo(
    () =>
      comparisonDateRange
        ? getDateRangeBounds(comparisonDateRange, comparisonCustomDateRange)
        : null,
    [
      comparisonDateRange,
      comparisonCustomDateRange?.start?.getTime(),
      comparisonCustomDateRange?.end?.getTime(),
    ]
  );

  // Convert to ISO strings for stable query variables
  const primaryDateFrom = dateBounds.start.toISOString();
  const primaryDateTo = dateBounds.end.toISOString();
  const comparisonDateFrom = comparisonDateBounds?.start.toISOString();
  const comparisonDateTo = comparisonDateBounds?.end.toISOString();

  // Fetch real phlebotomist stats from backend
  const primaryStatsQuery = useGetPhlebotomistStatsQuery({
    variables: {
      input: {
        dateFrom: primaryDateFrom,
        dateTo: primaryDateTo,
      },
    },
  });

  // Fetch comparison stats if comparison date range is provided
  const comparisonStatsQuery = useGetPhlebotomistStatsQuery({
    variables: {
      input: {
        dateFrom: comparisonDateFrom || new Date().toISOString(),
        dateTo: comparisonDateTo || new Date().toISOString(),
      },
    },
    skip: !comparisonDateBounds,
  });

  // Fetch Phlebotomist/TeamLead users for dropdown
  const usersQuery = useGetUsersListQuery({
    variables: {
      input: {
        filter: {
          roles: [UserRole.Phlebotomist, UserRole.TeamLead],
          isArchived: false,
        },
        take: 100,
      },
    },
  });

  // Fetch regions
  const regionsQuery = useGetRegionsQuery();

  // Fetch locations
  const locationsQuery = useGetLocationsQuery({
    variables: {
      input: {
        take: 1000,
      },
    },
  });

  // Log errors for debugging
  if (primaryStatsQuery.error) {
    console.error('primaryStatsQuery error:', primaryStatsQuery.error);
  }
  if (comparisonStatsQuery.error) {
    console.error('comparisonStatsQuery error:', comparisonStatsQuery.error);
  }

  const loading =
    primaryStatsQuery.loading ||
    usersQuery.loading ||
    regionsQuery.loading ||
    locationsQuery.loading ||
    (comparisonDateBounds && comparisonStatsQuery.loading);

  // Transform regions to options
  const regions: RegionOption[] = useMemo(() => {
    if (!regionsQuery.data?.getRegions) return [];
    return regionsQuery.data.getRegions.map((region) => ({
      id: region.id,
      name: region.name,
    }));
  }, [regionsQuery.data]);

  // Transform locations to options - always use real data
  const locations: LocationOption[] = useMemo(() => {
    if (!locationsQuery.data?.getLocations.data) return [];
    return locationsQuery.data.getLocations.data.map((location) => ({
      id: location.id,
      name: location.name,
      regionId: location.region?.id || null,
      regionName: location.region?.name || null,
    }));
  }, [locationsQuery.data]);

  // Transform users to phlebotomist options - always use real data
  const phlebotomists: PhlebotomistOption[] = useMemo(() => {
    if (!usersQuery.data?.getUsersList.data) return [];

    return usersQuery.data.getUsersList.data
      .map((user) => {
        const locationPrefs: string[] = user.locationPreferences
          ? user.locationPreferences
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
          : [];

        return {
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          regionId: user.productRegionPreference?.id || null,
          regionName: user.productRegionPreference?.name || null,
          locationPreferences: locationPrefs,
          assignedOfficeId: user.assignedOffice?.id || null,
          assignedOfficeName: user.assignedOffice?.name || null,
        };
      })
      .filter((ph) => !excludedUserIds.includes(ph.id));
  }, [usersQuery.data, excludedUserIds]);

  // Transform primary stats from backend
  const allStats: Map<string, PhlebotomistStats> = useMemo(() => {
    const statsMap = new Map<string, PhlebotomistStats>();

    if (!primaryStatsQuery.data?.getPhlebotomistStats) return statsMap;

    primaryStatsQuery.data.getPhlebotomistStats.forEach((backendStats) => {
      const stats: PhlebotomistStats = {
        phlebotomistId: backendStats.phlebotomistId,
        phlebotomistName: backendStats.phlebotomistName,
        phlebotomistEmail: backendStats.phlebotomistEmail,
        regionId: backendStats.regionId,
        regionName: backendStats.regionName,
        locationPreference: backendStats.locationPreference,
        assignedOfficeId: backendStats.assignedOfficeId,
        assignedOfficeName: backendStats.assignedOfficeName,
        attemptedDonations: backendStats.attemptedDonations,
        successfulDonations: backendStats.successfulDonations,
        pendingDonations: backendStats.pendingDonations ?? 0,
        singleDonors: backendStats.singleDonors,
        doubleDonors: backendStats.doubleDonors,
        successfulSingleDonors: backendStats.successfulSingleDonors,
        successfulDoubleDonors: backendStats.successfulDoubleDonors,
        expectedTotal: backendStats.expectedTotal,
        expectedPRBC: backendStats.expectedPRBC,
        expectedPRBCSingle: backendStats.expectedPRBCSingle ?? 0,
        expectedPRBCDouble: backendStats.expectedPRBCDouble ?? 0,
        expectedFFPSingle: backendStats.expectedFFPSingle,
        expectedFFPDouble: backendStats.expectedFFPDouble,
        actualTotal: backendStats.actualTotal,
        actualPRBC: backendStats.actualPRBC,
        actualPRBCSingle: backendStats.actualPRBCSingle ?? 0,
        actualPRBCDouble: backendStats.actualPRBCDouble ?? 0,
        actualFFPSingle: backendStats.actualFFPSingle,
        actualFFPDouble: backendStats.actualFFPDouble,
        dblDonorsSglYield: backendStats.dblDonorsSglYield,
        overallSuccessRate: backendStats.overallSuccessRate,
        prbcSuccessRate: backendStats.prbcSuccessRate,
        sglDonorsSuccessRate: backendStats.sglDonorsSuccessRate,
        dblDonorsSuccessRate: backendStats.dblDonorsSuccessRate,
        teamCount: backendStats.teamCount,
        totalOfficeSuccessful: backendStats.totalOfficeSuccessful,
        donationSharePercent: backendStats.donationSharePercent,
        officeSharePercent: backendStats.officeSharePercent,
        completelyFailedDonations: backendStats.completelyFailedDonations ?? 0,
        completelyFailedDiscards: (
          backendStats.completelyFailedDiscards ?? []
        ).map((dp: { reason: string; count: number }) => ({
          reason: dp.reason,
          count: dp.count,
        })),
        partiallyFailedDonations: backendStats.partiallyFailedDonations ?? 0,
        partiallyFailedDiscards: (
          backendStats.partiallyFailedDiscards ?? []
        ).map((dp: { reason: string; count: number }) => ({
          reason: dp.reason,
          count: dp.count,
        })),
        recentDonations: backendStats.recentDonations.map(
          transformDonationRecord
        ),
      };

      statsMap.set(stats.phlebotomistId, stats);
    });

    // Filter out excluded users
    for (const id of excludedUserIds) {
      statsMap.delete(id);
    }

    return statsMap;
  }, [primaryStatsQuery.data, excludedUserIds]);

  // Calculate office stats (grouped by assignedOffice)
  // Fallback: no office → region avg, no region → company avg
  const officeStats = useMemo(() => {
    return (phlebotomistId: string): ComparisonStats => {
      const selectedStats = allStats.get(phlebotomistId);
      if (!selectedStats?.assignedOfficeId) {
        // No office: fall back to region avg if phlebotomist has a region
        if (selectedStats?.regionId) {
          const regionPhlebotomists = Array.from(allStats.values()).filter(
            (s) => s.regionId === selectedStats.regionId
          );
          const region = regions.find((r) => r.id === selectedStats.regionId);
          return aggregateStats(
            regionPhlebotomists,
            `Region (${
              region?.name || selectedStats.regionName || 'Unknown'
            }) Avg`
          );
        }
        // No office and no region: fall back to company avg
        return aggregateStats(Array.from(allStats.values()), 'Company Avg');
      }

      // Filter phlebotomists who share the same assignedOffice
      const officePhlebotomistStats = Array.from(allStats.values()).filter(
        (s) => s.assignedOfficeId === selectedStats.assignedOfficeId
      );

      const label = `Office (${
        selectedStats.assignedOfficeName || 'Unknown'
      }) Avg`;
      return aggregateStats(officePhlebotomistStats, label);
    };
  }, [allStats, regions]);

  // Calculate region-specific stats
  const regionStats = useMemo(() => {
    return (regionId: string): ComparisonStats => {
      const regionPhlebotomists = Array.from(allStats.values()).filter(
        (s) => s.regionId === regionId
      );
      const region = regions.find((r) => r.id === regionId);
      return aggregateStats(
        regionPhlebotomists,
        `${region?.name || 'Region'} Avg`
      );
    };
  }, [allStats, regions]);

  // Calculate multi-region stats
  const multiRegionStats = useMemo(() => {
    return (regionIds: string[]): ComparisonStats => {
      if (regionIds.length === 0) {
        return aggregateStats([], 'Region Avg');
      }
      if (regionIds.length === 1) {
        const region = regions.find((r) => r.id === regionIds[0]);
        const regionPhlebotomists = Array.from(allStats.values()).filter(
          (s) => s.regionId === regionIds[0]
        );
        return aggregateStats(
          regionPhlebotomists,
          `${region?.name || 'Region'} Avg`
        );
      }
      const regionPhlebotomists = Array.from(allStats.values()).filter((s) =>
        regionIds.includes(s.regionId || '')
      );
      return aggregateStats(regionPhlebotomists, 'Multiple Regions Avg');
    };
  }, [allStats, regions]);

  // Calculate company-wide stats
  const companyStats: ComparisonStats = useMemo(() => {
    return aggregateStats(Array.from(allStats.values()), 'Company Avg');
  }, [allStats]);

  // Transform comparison stats from backend
  const comparisonAllStats: Map<string, PhlebotomistStats> = useMemo(() => {
    const statsMap = new Map<string, PhlebotomistStats>();

    if (!comparisonStatsQuery.data?.getPhlebotomistStats) return statsMap;

    comparisonStatsQuery.data.getPhlebotomistStats.forEach((backendStats) => {
      const stats: PhlebotomistStats = {
        phlebotomistId: backendStats.phlebotomistId,
        phlebotomistName: backendStats.phlebotomistName,
        phlebotomistEmail: backendStats.phlebotomistEmail,
        regionId: backendStats.regionId,
        regionName: backendStats.regionName,
        locationPreference: backendStats.locationPreference,
        assignedOfficeId: backendStats.assignedOfficeId,
        assignedOfficeName: backendStats.assignedOfficeName,
        attemptedDonations: backendStats.attemptedDonations,
        successfulDonations: backendStats.successfulDonations,
        pendingDonations: backendStats.pendingDonations ?? 0,
        singleDonors: backendStats.singleDonors,
        doubleDonors: backendStats.doubleDonors,
        successfulSingleDonors: backendStats.successfulSingleDonors,
        successfulDoubleDonors: backendStats.successfulDoubleDonors,
        expectedTotal: backendStats.expectedTotal,
        expectedPRBC: backendStats.expectedPRBC,
        expectedPRBCSingle: backendStats.expectedPRBCSingle ?? 0,
        expectedPRBCDouble: backendStats.expectedPRBCDouble ?? 0,
        expectedFFPSingle: backendStats.expectedFFPSingle,
        expectedFFPDouble: backendStats.expectedFFPDouble,
        actualTotal: backendStats.actualTotal,
        actualPRBC: backendStats.actualPRBC,
        actualPRBCSingle: backendStats.actualPRBCSingle ?? 0,
        actualPRBCDouble: backendStats.actualPRBCDouble ?? 0,
        actualFFPSingle: backendStats.actualFFPSingle,
        actualFFPDouble: backendStats.actualFFPDouble,
        dblDonorsSglYield: backendStats.dblDonorsSglYield,
        overallSuccessRate: backendStats.overallSuccessRate,
        prbcSuccessRate: backendStats.prbcSuccessRate,
        sglDonorsSuccessRate: backendStats.sglDonorsSuccessRate,
        dblDonorsSuccessRate: backendStats.dblDonorsSuccessRate,
        teamCount: backendStats.teamCount,
        totalOfficeSuccessful: backendStats.totalOfficeSuccessful,
        donationSharePercent: backendStats.donationSharePercent,
        officeSharePercent: backendStats.officeSharePercent,
        completelyFailedDonations: backendStats.completelyFailedDonations ?? 0,
        completelyFailedDiscards: (
          backendStats.completelyFailedDiscards ?? []
        ).map((dp: { reason: string; count: number }) => ({
          reason: dp.reason,
          count: dp.count,
        })),
        partiallyFailedDonations: backendStats.partiallyFailedDonations ?? 0,
        partiallyFailedDiscards: (
          backendStats.partiallyFailedDiscards ?? []
        ).map((dp: { reason: string; count: number }) => ({
          reason: dp.reason,
          count: dp.count,
        })),
        recentDonations: backendStats.recentDonations.map(
          transformDonationRecord
        ),
      };

      statsMap.set(stats.phlebotomistId, stats);
    });

    // Filter out excluded users
    for (const id of excludedUserIds) {
      statsMap.delete(id);
    }

    return statsMap;
  }, [comparisonStatsQuery.data, excludedUserIds]);

  // Calculate comparison office stats (grouped by assignedOffice)
  const comparisonOfficeStats = useMemo(() => {
    return (phlebotomistId: string): ComparisonStats => {
      if (comparisonAllStats.size === 0) {
        return aggregateStats([], 'Office Avg');
      }

      const selectedStats = comparisonAllStats.get(phlebotomistId);
      if (!selectedStats?.assignedOfficeId) {
        return aggregateStats(
          Array.from(comparisonAllStats.values()),
          'Office Avg'
        );
      }

      const officePhlebotomistStats = Array.from(
        comparisonAllStats.values()
      ).filter((s) => s.assignedOfficeId === selectedStats.assignedOfficeId);

      const label = `Office (${
        selectedStats.assignedOfficeName || 'Unknown'
      }) Avg`;
      return aggregateStats(officePhlebotomistStats, label);
    };
  }, [comparisonAllStats]);

  // Calculate comparison region-specific stats
  const comparisonRegionStats = useMemo(() => {
    return (regionId: string): ComparisonStats => {
      if (comparisonAllStats.size === 0) {
        return aggregateStats([], 'Region Avg');
      }

      const regionPhlebotomists = Array.from(
        comparisonAllStats.values()
      ).filter((s) => s.regionId === regionId);
      const region = regions.find((r) => r.id === regionId);
      return aggregateStats(
        regionPhlebotomists,
        `${region?.name || 'Region'} Avg`
      );
    };
  }, [comparisonAllStats, regions]);

  // Calculate comparison multi-region stats
  const comparisonMultiRegionStats = useMemo(() => {
    return (regionIds: string[]): ComparisonStats => {
      if (comparisonAllStats.size === 0 || regionIds.length === 0) {
        return aggregateStats([], 'Region Avg');
      }
      if (regionIds.length === 1) {
        const region = regions.find((r) => r.id === regionIds[0]);
        const regionPhlebotomists = Array.from(
          comparisonAllStats.values()
        ).filter((s) => s.regionId === regionIds[0]);
        return aggregateStats(
          regionPhlebotomists,
          `${region?.name || 'Region'} Avg`
        );
      }
      const regionPhlebotomists = Array.from(
        comparisonAllStats.values()
      ).filter((s) => regionIds.includes(s.regionId || ''));
      return aggregateStats(regionPhlebotomists, 'Multiple Regions Avg');
    };
  }, [comparisonAllStats, regions]);

  // Calculate comparison company-wide stats
  const comparisonCompanyStats: ComparisonStats = useMemo(() => {
    if (comparisonAllStats.size === 0) {
      return aggregateStats([], 'Company Avg');
    }
    return aggregateStats(
      Array.from(comparisonAllStats.values()),
      'Company Avg'
    );
  }, [comparisonAllStats]);

  // Full aggregated stats for side-by-side comparison cards
  const regionFullStats = useMemo(() => {
    return (regionId: string): PhlebotomistStats => {
      const regionPhlebotomists = Array.from(allStats.values()).filter(
        (s) => s.regionId === regionId
      );
      const region = regions.find((r) => r.id === regionId);
      return aggregateFullStats(
        regionPhlebotomists,
        `${region?.name || 'Region'} Avg`
      );
    };
  }, [allStats, regions]);

  const multiRegionFullStats = useMemo(() => {
    return (regionIds: string[]): PhlebotomistStats => {
      if (regionIds.length === 0) {
        return aggregateFullStats([], 'Region Avg');
      }
      if (regionIds.length === 1) {
        const region = regions.find((r) => r.id === regionIds[0]);
        const regionPhlebotomists = Array.from(allStats.values()).filter(
          (s) => s.regionId === regionIds[0]
        );
        return aggregateFullStats(
          regionPhlebotomists,
          `${region?.name || 'Region'} Avg`
        );
      }
      const regionPhlebotomists = Array.from(allStats.values()).filter((s) =>
        regionIds.includes(s.regionId || '')
      );
      return aggregateFullStats(regionPhlebotomists, 'Multiple Regions Avg');
    };
  }, [allStats, regions]);

  const companyFullStats: PhlebotomistStats = useMemo(() => {
    return aggregateFullStats(Array.from(allStats.values()), 'Company Avg');
  }, [allStats]);

  return {
    phlebotomists,
    regions,
    locations,
    allStats,
    officeStats,
    regionStats,
    multiRegionStats,
    companyStats,
    regionFullStats,
    multiRegionFullStats,
    companyFullStats,
    comparisonAllStats,
    comparisonOfficeStats,
    comparisonRegionStats,
    comparisonMultiRegionStats,
    comparisonCompanyStats,
    loading: !!loading,
  };
}
